import moment from "moment";
import { UploadLiquidBillingInvoices } from "../../../../services/requests/billing/liquid-billing-requests";
import { FormItemType } from "../../../UI/view/component-map";
import { FilterType, ListView, ListViewColumnType, connectListView } from "../../../UI/view/list-view";
import { liquidMainDataSource } from "./liquid-billing-datasource";
import { CreateTransfer, CreateTransferDataSource } from "./liquid-create-transfer";
import { addDaysToDate, formatDate } from "../../../../services/format";
import { LogicalConnector } from "../../../UI/view/data-source";

export enum LiquidStatus {
  Bypassed,
  Processed,
  UnlinkedCreditInvoices,
  PendingAutomaticUpload,
  CreditInvoices,
  PendingDelivery,
  PendingManualUpload,
  Errors
}


const jdeVolume = `(LiquidBillingDetails.First(ValueType == "NV").OverrideValue 
    ?? LiquidBillingDetails.First(ValueType == "NV").Value 
    ?? 0)`;

const isActive = `(!Jdebypassed)`;
const isStockTransfer = `(DocType == "ST")`;
const isNotStockTransfer = `!(DocType == "ST")`;
const isNetPositive = `(LiquidBillingDetails.Any(ValueType == "NV" && (OverrideValue ?? Value) > 0))`;
const isNetNegative = `(LiquidBillingDetails.Any(ValueType == "NV" && (OverrideValue ?? Value) < 0))`;
const isPendingDeliveryStatus = '(Status == 0 && LiquidTrackingNumber.Location != null)';
const isStaging = `(!LiquidCustomer.LinkCreditDebit || (StagingInvoice == 1 && LiquidTrackingNumber.LiquidStagingInventory.FirstOrDefault(FullCustomerResidualBilled).FullResidualVolumeCalculated != LiquidBillingDetails.Where(ValueType == "NV").Sum(Value)))`;
const isManual = '(Status == 1 && LiquidTrackingNumber.Location != null)';
const isLinkCreditDebit = `(LiquidCustomer.LinkCreditDebit)`;
const isUnlinked = `(StagingInvoice == 0 || LiquidTrackingNumber.LiquidStagingInventory.FirstOrDefault(FullCustomerResidualBilled).FullResidualVolumeCalculated == LiquidBillingDetails.Where(ValueType == "NV").Sum(Value * -1))`;

/* const isActiveFn = (Jdebypassed) => !Jdebypassed;
const isStockTransferFn = (DocType) => DocType == "ST";
const isNotStockTransferFn = (DocType) => DocType != "ST";
const isNetPositiveFn = (LiquidBillingDetails, ValueType, OverrideValue, Value) => LiquidBillingDetails.Any(ValueType == "NV" && (OverrideValue ?? Value) > 0);
const isNetNegativeFn = (LiquidBillingDetails, ValueType, OverrideValue, Value) => LiquidBillingDetails.Any(ValueType == "NV" && (OverrideValue ?? Value) < 0);
const isPendingDeliveryStatusFn = (Status, LiquidTrackingNumber) => (Status == 0 && LiquidTrackingNumber.Location != null);
const isStagingFn = (LiquidCustomer, StagingInvoice, LiquidTrackingNumber, LiquidBillingDetails) => (!LiquidCustomer.LinkCreditDebit || (StagingInvoice == 1 && LiquidTrackingNumber.LiquidStagingInventory.FirstOrDefault(FullCustomerResidualBilled).FullResidualVolumeCalculated != LiquidBillingDetails.Where(ValueType == "NV").Sum(Value)));
const isManualFn = (Status, LiquidTrackingNumber) => (Status == 1 && LiquidTrackingNumber.Location != null);
const isLinkCreditDebitFn = (LiquidCustomer) => (LiquidCustomer.LinkCreditDebit);
const isUnlinkedFn = (StagingInvoice, LiquidTrackingNumber, LiquidBillingDetails) => (StagingInvoice == 0 || LiquidTrackingNumber.LiquidStagingInventory.FirstOrDefault(FullCustomerResidualBilled).FullResidualVolumeCalculated == LiquidBillingDetails.Where(ValueType == "NV").Sum(Value * -1));
*/

const isPendingDelivery = `(${isActive} && ${isNotStockTransfer} && ${isPendingDeliveryStatus} && ${isNetPositive})`;
const isPendingCredit = `(${isActive} && ${isNotStockTransfer} && ${isNetNegative} && ${isStaging})`;
const isAutomaticUpload = `(${isActive} && ${isStockTransfer})`;
const isManualUploadInvoice = `(${isActive} && ${isNotStockTransfer} && ${isManual} && ${isNetPositive})`;
const isErrors = `(${isActive} && Status == 9)`;
const isBypassed = `(JdeBypassed)`;
const isProcessed = '(Status == 5 && !JdeBypassed)';

const createIff = (arr: { value: LiquidStatus; expression: string }[]) => {
  if (arr.length == 0) return '-1';
  if (arr.length == 1) return arr[0].value.toString();
  const [first, ...rest] = arr;
  return `iif(${first.expression}, ${first.value}, ${createIff(rest)})`;
}

const correctOrderOps = [
  {
    value: LiquidStatus.Errors,
    expression: isErrors,
    icon: 'exclamation-diamond',
    tooltip: 'Error',
    uploadEnabled: true,
  },
  {
    value: LiquidStatus.Processed,
    expression: isProcessed,
    icon: 'check',
    tooltip: 'Processed',
  },
  {
    value: LiquidStatus.Bypassed,
    expression: isBypassed,
    icon: 'arrow-return-left',
    tooltip: 'Bypassed'
  },
  {
    value: LiquidStatus.PendingManualUpload,
    expression: isManualUploadInvoice,
    icon: 'currency-dollar',
    tooltip: 'Pending Manual Upload',
    uploadEnabled: true,
  },
  {
    value: LiquidStatus.PendingDelivery,
    expression: isPendingDelivery,
    icon: 'clock',
    tooltip: 'Pending Delivery',
    uploadEnabled: true,
  },
  {
    value: LiquidStatus.CreditInvoices,
    expression: isPendingCredit,
    icon: 'credit-card',
    tooltip: 'Pending Credit Invoices',
    uploadEnabled: true,
  },
  {
    value: LiquidStatus.PendingAutomaticUpload,
    expression: isAutomaticUpload,
    icon: 'gear',
    tooltip: 'Pending Automatic Upload'
  },
]

export const liquidListFilters = [
  {
    type: FilterType.combo,
    fieldName: 'shippingPlant',
    label: 'Plant',
    options: (state) => {
      return state?.liquidbilling?.liquidLocations?.data?.map(loc => loc.locationName)?.sort() || [];
    },
    serverExpression: 'IIF(StagingInvoice == 1, LiquidTrackingNumber.LiquidStagingInventory.FirstOrDefault().Location.LocationName, LiquidTrackingNumber.Location.LocationName) == "@val"',
    logicalConnector: LogicalConnector.And,
  },
  {
    type: FilterType.simple,
    fieldName: 'unbilled',
    label: 'Unbilled',
    filterValue: false,
    serverExpression: 'Status == 0 && Jdebypassed == false',
    logicalConnector: LogicalConnector.And,
  },
  {
    type: FilterType.simple,
    fieldName: 'hasErrors',
    label: 'JDE Errors',
    filterValue: false,
    serverExpression: 'Status == 9 && Jdebypassed == false',
    logicalConnector: LogicalConnector.Or,
  },
  {
    type: FilterType.simple,
    fieldName: 'jdeBypassed',
    label: 'JDE Bypassed',
    filterValue: false,
    serverExpression: 'Jdebypassed == true',
    logicalConnector: LogicalConnector.Or
  },
  {
    type: FilterType.simple,
    fieldName: 'jdeUploaded',
    label: 'JDE Uploaded',
    filterValue: false,
    serverExpression: 'Status == 5 and jdebatchnumber != null',
    logicalConnector: LogicalConnector.Or
  },
  {
    type: FilterType.number,
    fieldName: 'dateInvoiced',
    label: 'Show Last X Days',
    filterValue: null,
    maxValue: 180,
    minValue: 1,
    serverExpression: 'DateInvoiced >= @val',
    valExpression: (val) => {
      return '"' + formatDate(addDaysToDate(new Date(), -val)) + '"';
    },
    logicalConnector: LogicalConnector.And
  },
  {
    type: FilterType.simple,
    fieldName: 'holdDate',
    label: 'On Hold',
    filterValue: false,
    serverExpression: 'holdDate > ' + '"' + formatDate(moment(moment().startOf('day'))) + '"',
    logicalConnector: LogicalConnector.Or
  },
];
const state = createIff(correctOrderOps);
export const liquidBillingListColumns = [
  {
    Header: '',
    accessor: 'checked',
    type: ListViewColumnType.checkbox,
    excludeGlobalFilter: true,
  },

  {
    type: ListViewColumnType.icon,
    id: 'trafficStatus',
    excludeGlobalFilter: true,
    accessor: ({ invoiceState }) => correctOrderOps.find(x => x.value == invoiceState)?.icon,
    tooltip: ({ invoiceState }) => correctOrderOps.find(x => x.value == invoiceState)?.tooltip || '',
  },
  {
    accessor: 'jdeBatchNumber',
    Header: 'Batch Number'
  },
  {
    accessor: 'orderTypeName',
    Header: 'Order Type',
    excludeGlobalFilter: true,
    serverExpression: 'iif(DocType == "ST", "Stock Transfer", "Bulk")'
  },
  {
    accessor: 'shippingPlant',
    Header: 'Plant',
    excludeGlobalFilter: true,
    disableSortBy: true,
  },
  {
    accessor: 'customerName',
    Header: 'Customer Name',
    type: ListViewColumnType.linkEdit,
    serverExpression: 'LiquidCustomer.customerName',
    linkPath: ({ row }) => `/billing/liquidedit/${row.original.billingId}`
  },
  {
    accessor: 'customerId',
    Header: 'PX Number'
  },
  {
    accessor: 'dateJdeprocessed',
    Header: 'Date JDE Processed',
    excludeGlobalFilter: true,
    type: ListViewColumnType.date
  },
  {
    accessor: 'dateShipped',
    Header: 'Date Shipped',
    excludeGlobalFilter: true, 
    serverExpression: `${jdeVolume} > 0 ? liquidTrackingNumber.liquidShipping.datetime : null`,
    type: ListViewColumnType.date,
  },
  {
    accessor: 'dateStarted',
    Header: 'Arrival Date',
    type: ListViewColumnType.date,
    excludeGlobalFilter: true,
  },
  {
    accessor: 'jdeVolume',
    Header: 'Volume',
    serverExpression: jdeVolume,
    excludeGlobalFilter: true,
  },
  {
    accessor: 'containerIdstarted',
    Header: 'Container Number'
  },
  {
    accessor: 'liquidCustomer.city',
    Header: 'City'
  },
  {
    accessor: 'liquidCustomer.state',
    Header: 'State'
  },
  {
    accessor: 'liquidCustomer.isNalscustomer',
    Header: 'NALS',
    type: ListViewColumnType.yesno
  },
  {
    Header: 'JDE Errors',
    id: 'uploadIssues',
    serverExpression: 'uploadIssues ?? jdeRejectReason',
    accessor: ({ uploadIssues, jderejectReason }) => uploadIssues || jderejectReason,
    hideOverflow: true,
    maxWidth: 200,
  },
  {
    accessor: 'billingId',
    Header: '',
    hidden: true
  },
/*   {
    accessor: 'jdeBatchNumber',
    Header: '',
    hidden: true
  }, */
  {
    accessor: 'ronumber',
    Header: '',
    hidden: true
  },
  {
    accessor: 'legacyBillingId',
    Header: '',
    hidden: true
  },
  {
    Header: 'priority',
    accessor: 'priority',
    hidden: true,
    defaultSort: true,
    excludeGlobalFilter: true,
    defaultSortDirection: 'desc',
    serverExpression: 'dateJdeprocessed ?? dateInvoiced ?? dateJdebypassed'
  },
];

export const liquidBillingListToolbar = (tblName) => [
  {
    type: FormItemType.button,
    modal: 'createTransfer',
    label: 'Create Transfer',
  },
  {
    type: FormItemType.button,
    label: 'Upload Selected to JDE',
    permissions: {
      read: [1, 14, 15, 18],
      write: [1, 15],
      admin: [1, 15],
    },
    handler: async ({ getValues, confirm, loadData }) => {
      const vals = getValues();
      const billingList = vals[tblName];
      if (!billingList?.length) {
        await confirm('No invoices are selected. Please select invoices from the list below then press upload.', true)
      } else {
        if (await confirm("Are you sure you want to upload the selected invoices to JDE?") == true) {
          let isError = false;
          try {
            await UploadLiquidBillingInvoices({ billingIds: billingList.map(b => b.billingId) });
          } catch (e) {
            isError = true;
            await confirm('There was an error uploading to JDE:' + e, true)
          }
          if (!isError) {
            await confirm('JDE Upload process is completed.', true);
            loadData();
          }
        }
      }
    }
  }
];

export const liquidBillingInvoices = {
  name: 'liquidBillingList',
  additionalModals: [
    {
      form: CreateTransfer,
      repository: CreateTransferDataSource,
      name: 'createTransfer',
      title: 'Create Transfer',
    }
  ],
  secondarySort: '(dateJdeprocessed ?? dateInvoiced) desc',
  toolbar: liquidBillingListToolbar('liquidBillingList'),
  columns: liquidBillingListColumns.filter(x => x.accessor != 'checked' && x.id != 'trafficStatus'),
  filters: liquidListFilters,
} as ListView;


export const [LiquidBillingInvoices, LiquidBillingInvoiceDataSource] = connectListView(liquidBillingInvoices, liquidMainDataSource);